<template>
  <div
    ref="containerRef"
    class="sidetone-container"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave">

    <!-- 耳返按钮 -->
    <div class="hover-button">
      <a href="javascript:;" @click="toggleSidetone">
        <img
          alt="耳返"
          class="footer-icon"
          :src="sidetoneIconSrc" />
      </a>
    </div>

    <!-- 悬浮窗 -->
    <Transition name="popup-fade">
      <div
        v-show="showPopup"
        class="sidetone-popup-window"
        :class="{ 'force-show': isDragging }"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave">

        <div class="sidetone-popup-content">
          <div class="sidetone-slider-container">
            <div
              class="sidetone-slider-track"
              @mousedown="handleThumbMouseDown"
              @touchstart="handleThumbTouchStart">
              <div
                class="sidetone-slider-progress"
                :style="{ height: `${sidetoneVolume}%` }"></div>
              <div
                ref="thumbRef"
                class="sidetone-slider-thumb"
                :style="{ bottom: `${sidetoneVolume}%` }"></div>
            </div>
            <div class="sidetone-value">{{ sidetoneVolume }}%</div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { useDebounceFn } from '@vueuse/core'

const deviceStore = useDeviceStore()

const containerRef = ref<HTMLElement>()
const thumbRef = ref<HTMLElement>()
const showPopup = ref(false)
const isDragging = ref(false)
const sidetoneBusy = ref(false)
let closeTimer: NodeJS.Timeout | null = null

// 耳返状态
const sidetoneEnabled = computed(() =>
  deviceStore.deviceStatus?.sidetoneEnabled === 1
)
const sidetoneVolume = computed(() =>
  deviceStore.deviceStatus?.sidetoneVolume || 0
)

// 耳返图标
const sidetoneIconSrc = computed(() => {
  if (sidetoneEnabled.value) {
    return '/img/footer/footer_sidetone_on_0824.png'
  } else {
    return '/img/footer/footer_sidetone_off_0824.png'
  }
})

// 鼠标进入显示悬浮窗
function handleMouseEnter() {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  showPopup.value = true
}

// 鼠标离开隐藏悬浮窗（除非正在拖动）
function handleMouseLeave() {
  if (isDragging.value) {
    return // 拖动时不隐藏
  }

  closeTimer = setTimeout(() => {
    showPopup.value = false
  }, 300)
}

function showGlobalLightTip(message: string): void {
  window.dispatchEvent(new CustomEvent<string>('app-light-tip', { detail: message }))
}

// 切换耳返开关：提示只以硬件 0x80 最终确认结果为准，不能以点击动作本身为准。
async function toggleSidetone() {
  if (sidetoneBusy.value) return
  sidetoneBusy.value = true
  const target = !sidetoneEnabled.value
  try {
    const confirmed = await deviceStore.setSidetoneEnabled(target)
    if (confirmed) {
      showGlobalLightTip(target ? 'Sidetone enabled.' : 'Sidetone disabled.')
      return
    }

    // 产品交互要求：开启没有被硬件确认时，直接提示用户连接外置麦。
    showGlobalLightTip(
      target
        ? 'Connect the external microphone to enable sidetone.'
        : 'Sidetone could not be disabled.',
    )
  } catch (error) {
    console.error('[Sidetone] 切换失败:', error)
    showGlobalLightTip(
      target
        ? 'Connect the external microphone to enable sidetone.'
        : 'Sidetone could not be disabled.',
    )
  } finally {
    sidetoneBusy.value = false
  }
}

// 音量调节（防抖）
const debouncedSetVolume = useDebounceFn(async (volume: number) => {
  await deviceStore.setSidetoneVolume(volume)
}, 200)

// 滑块拖动处理
let startY = 0
let startVolume = 0
let trackRect: DOMRect | null = null

function handleThumbMouseDown(e: MouseEvent) {
  e.preventDefault()
  isDragging.value = true
  startY = e.clientY
  startVolume = sidetoneVolume.value

  // 获取轨道位置
  const track = thumbRef.value?.parentElement
  if (track) {
    trackRect = track.getBoundingClientRect()
  }

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)

  // 强制保持悬浮窗显示
  showPopup.value = true
}

function handleThumbTouchStart(e: TouchEvent) {
  e.preventDefault()
  isDragging.value = true
  startY = e.touches[0].clientY
  startVolume = sidetoneVolume.value

  const track = thumbRef.value?.parentElement
  if (track) {
    trackRect = track.getBoundingClientRect()
  }

  document.addEventListener('touchmove', handleTouchMove, { passive: false })
  document.addEventListener('touchend', handleTouchEnd)

  showPopup.value = true
}

function handleMouseMove(e: MouseEvent) {
  if (!isDragging.value || !trackRect) return

  // 计算新音量（向上拖动增加音量）
  const deltaY = startY - e.clientY
  const deltaPercent = (deltaY / trackRect.height) * 100
  const newVolume = Math.max(0, Math.min(100, Math.round(startVolume + deltaPercent)))

  // 立即更新UI
  if (deviceStore.deviceStatus) {
    deviceStore.deviceStatus.sidetoneVolume = newVolume
  }

  // 防抖发送命令
  debouncedSetVolume(newVolume)
}

function handleTouchMove(e: TouchEvent) {
  if (!isDragging.value || !trackRect) return

  e.preventDefault()
  const deltaY = startY - e.touches[0].clientY
  const deltaPercent = (deltaY / trackRect.height) * 100
  const newVolume = Math.max(0, Math.min(100, Math.round(startVolume + deltaPercent)))

  if (deviceStore.deviceStatus) {
    deviceStore.deviceStatus.sidetoneVolume = newVolume
  }

  debouncedSetVolume(newVolume)
}

function handleMouseUp() {
  isDragging.value = false
  trackRect = null

  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)

  // 延迟隐藏悬浮窗
  setTimeout(() => {
    if (!isDragging.value) {
      handleMouseLeave()
    }
  }, 100)
}

function handleTouchEnd() {
  isDragging.value = false
  trackRect = null

  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)

  setTimeout(() => {
    if (!isDragging.value) {
      handleMouseLeave()
    }
  }, 100)
}

onUnmounted(() => {
  if (closeTimer) {
    clearTimeout(closeTimer)
  }
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  document.removeEventListener('touchmove', handleTouchMove)
  document.removeEventListener('touchend', handleTouchEnd)
})
</script>

<style scoped>
.sidetone-container {
  position: relative;
  display: flex;
  width: 54px;
  height: 54px;
  align-items: center;
  justify-content: center;
}

.sidetone-container .hover-button,
.sidetone-container .hover-button > a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  line-height: 0;
}

.sidetone-container .hover-button {
  cursor: pointer;
}

.footer-icon {
  width: 52px;
  height: 52px;
  display: block;
}

.sidetone-container .hover-button:hover .footer-icon {
  content: url('/img/footer/footer_sidetone_on_hover_0824.png');
}

.sidetone-container .hover-button:hover .footer-icon[src$='footer_sidetone_off_0824.png'] {
  content: url('/img/footer/footer_sidetone_off_hover_0824.png');
}

/* 悬浮窗 */
.sidetone-popup-window {
  position: absolute;
  bottom: 100%;
  left: 52%;
  z-index: 10;
  width: 55px;
  box-sizing: border-box;
  transform: translateX(-50%);
  margin-bottom: 22px;
  padding: 18px 25px;
  border-radius: 13px;
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  font-size: 12px;
}

.sidetone-popup-window.force-show {
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
}

.sidetone-popup-window::after {
  display: none;
}

.sidetone-popup-content {
  width: 6px;
}

/* 滑块容器 */
.sidetone-slider-container {
  width: 6px;
}

.sidetone-slider-track {
  position: relative;
  width: 6px;
  height: 130px;
  background: rgba(35, 35, 41, 1);
  border-radius: 999px;
  cursor: pointer;
}

.sidetone-slider-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #376bed;
  border-radius: 999px;
  transition: height 0.1s ease;
}

.sidetone-slider-thumb {
  position: absolute;
  left: 50%;
  transform: translate(-50%, 50%);
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  border: 2px solid #fff;
  background: #376bed;
  border-radius: 50%;
  cursor: pointer;
  transition: 0.1s;
}

.sidetone-value {
  position: relative;
  top: 10px;
  left: -11px;
  width: 29px;
  margin-bottom: 10px;
  color: rgba(255, 255, 255, 1);
  font-size: 13px;
  text-align: center;
}

/* 动画 */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

</style>
